/**
 * UX-02 — query-string transforms for the three legacy report routes that
 * now redirect into the /insights hub. Kept as pure functions (rather than
 * inline in App.tsx) so they're unit-testable from insights-hub.test.tsx.
 */

/** /health → /insights?tab=reliability, carrying since/project (and
 *  anything else, e.g. ?stack=/?drill=) through unchanged — Health's
 *  `since` values already are the hub's AnalyticsRange. */
export function healthRedirectTransform(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set('tab', 'reliability');
  return next;
}

/** /reports/spend → /insights?tab=spend, renaming Spend's own inner
 *  `?tab=breakdown|forecast` to `?view=` so it doesn't collide with the
 *  hub's `?tab=`. Everything else (since/period/from/to/groupBy/project/
 *  org/repo) carries through unchanged. */
export function spendRedirectTransform(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  next.set('tab', 'spend');
  for (const [key, value] of params) {
    if (key === 'tab') next.set('view', value);
    else next.set(key, value);
  }
  return next;
}

/** /analytics/cost-efficiency → /insights?tab=efficiency, mapping its
 *  `?range=` (30d|90d|180d|1y|all) onto the hub's `?since=`
 *  (7d|30d|90d|all). The hub has no 180d/1y bucket, so both fold into
 *  "all" — the closest superset. Missing `range` (page default was 90d)
 *  maps to the hub's `since=90d`. `project`/`repo`/`legacy`/`sort` carry
 *  through unchanged. */
const EFFICIENCY_RANGE_TO_SINCE: Record<string, string> = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  '180d': 'all',
  '1y': 'all',
  all: 'all',
};

export function efficiencyRedirectTransform(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  next.set('tab', 'efficiency');
  let sawRange = false;
  for (const [key, value] of params) {
    if (key === 'range') {
      sawRange = true;
      next.set('since', EFFICIENCY_RANGE_TO_SINCE[value] ?? '90d');
    } else {
      next.set(key, value);
    }
  }
  if (!sawRange) next.set('since', '90d');
  return next;
}
